import React, { useEffect, useRef, useCallback, useMemo } from "react";
import { Container } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import {
  closeIntroPopup,
  setIntroTransitionActive,
  setShowGameAfterTransition,
} from "../../../State/actions";
import {
  selectIntroTransitionActive,
  selectIsFeatureActive,
} from "../../../State/selectors";

import { useDimensions } from "../../../Contexts/DimensionsContext";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";

import { Settings } from "../../../settings";
import anime from "animejs";
import * as PIXI from "pixi.js";

const isPrimaryTheme = Settings.APP.UI_THEME === "primary";

const IntroTransitionOverlay = () => {
  const dispatch = useDispatch();

  const { dimensions } = useDimensions();
  const { backgroundAnimationControllerRef } = useReelRefs();
  const overlayRef = useRef(null);
  const animationRef = useRef(null);
  const introTransitionActive = useSelector(selectIntroTransitionActive);
  const isFeatureActive = useSelector(selectIsFeatureActive);
  const { backgroundMusicEnabled } = useSelector((state) => state.sound);

  // Memoize container zIndex
  const containerZIndex = useMemo(() => 10000, []); // Above absolutely everything

  /**
   * Create overlay sprite — theme-aware:
   * Primary: diagonal gradient matching the primary loader (#2a1f5e → #150e3a → #050020)
   * Secondary: solid black matching the secondary loader
   */
  const createOverlaySprite = useCallback(() => {
    const canvas = document.createElement("canvas");
    canvas.width = dimensions.width;
    canvas.height = dimensions.height;
    const ctx = canvas.getContext("2d");

    if (isPrimaryTheme) {
      // Diagonal linear gradient (top-left → bottom-right) — matches primary loader
      const linear = ctx.createLinearGradient(0, 0, dimensions.width, dimensions.height);
      linear.addColorStop(0, "#2a1f5e");
      linear.addColorStop(0.4, "#150e3a");
      linear.addColorStop(1, "#050020");
      ctx.fillStyle = linear;
    } else {
      // Solid black — matches secondary loader
      ctx.fillStyle = "#000000";
    }
    ctx.fillRect(0, 0, dimensions.width, dimensions.height);

    const texture = PIXI.Texture.from(canvas);
    const sprite = new PIXI.Sprite(texture);
    sprite.width = dimensions.width;
    sprite.height = dimensions.height;
    return sprite;
  }, [dimensions.width, dimensions.height]);

  // Handle transition animation
  useEffect(() => {
    if (introTransitionActive && overlayRef.current) {
      // Clear any existing animation
      if (animationRef.current) {
        animationRef.current.pause();
      }

      // Remove old overlay and create new one
      overlayRef.current.removeChildren();
      overlayRef.current.addChild(createOverlaySprite());

      // Create cinematic transition: fade in black → pause → fade out to reveal game
      const animation = anime.timeline({
        complete: () => {
          dispatch(setIntroTransitionActive(false));
        },
      });

      // Phase 1: Fade in black overlay to cover the intro screen
      animation.add({
        targets: overlayRef.current,
        alpha: [0, 1],
        duration: 200,
        easing: "easeInQuad",
        complete: () => {
          // Screen is now entirely black - clear background animation and remove intro
          const bgController = backgroundAnimationControllerRef.current;
          if (bgController?.backgroundSpine) {
            // Clear all animation tracks
            if (bgController.backgroundSpine.state) {
              bgController.backgroundSpine.state.clearTracks();
            }
            // Remove front_mask slot attachments (flowers/tree) so they disappear
            bgController.stopFrontMask();
            // Use setBonesToSetupPose() instead of setToSetupPose() — resets bones without slot attachment changes (no DarkSlotMesh leak)
            bgController.backgroundSpine.skeleton.setBonesToSetupPose();
            // Play appropriate idle animation
            if (isFeatureActive) {
              bgController.playFeatureIdle?.();
            } else {
              bgController.playBaseIdle?.();
            }
          }

          // Remove intro and show game underneath
          dispatch(closeIntroPopup());
          dispatch(setShowGameAfterTransition(true));
        },
      });

      // Phase 2: Brief dramatic pause with full black coverage
      animation.add({
        targets: overlayRef.current,
        alpha: 1,
        duration: 200,
      });

      // Phase 3: Fade out black overlay to reveal the game
      animation.add({
        targets: overlayRef.current,
        alpha: [1, 0],
        duration: 200,
        easing: "easeOutQuad",
        complete: () => {
          // Music is already started by releaseMusicPlayback() in IntroPopupComposed
        },
      });

      animationRef.current = animation;

      return () => {
        if (animationRef.current) {
          animationRef.current.pause();
          animationRef.current = null;
        }
      };
    }
  }, [
    introTransitionActive,
    createOverlaySprite,
    dispatch,
    backgroundAnimationControllerRef,
    isFeatureActive,
    backgroundMusicEnabled,
  ]);

  // Only render when there's an active transition
  if (!introTransitionActive) return null;

  return (
    <Container
      name="intro_transition_overlay"
      ref={overlayRef}
      x={0}
      y={0}
      zIndex={containerZIndex}
    />
  );
};

export default IntroTransitionOverlay;
